import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { Suspense } from 'react';
import { diamondGlows, DiamondGrid, Squiggle } from '../primitives';
import { Stats } from './stats';

export function Hero() {
	const { t } = useLingui();
	return (
		<section className="relative overflow-hidden">
			{/* Figma's three hero glows. Two sit almost entirely off-canvas, which is what makes the texture
			    densest at the screen edges; the third is low and centred, and brings it back across the
			    middle under the CTAs. Y values are 95px less than the export's because those are page
			    coordinates and this section starts below the header. `opacity` 1: the glows' own 0.4 alpha
			    is the intensity, so dimming again would double-fade it. */}
			<DiamondGrid
				className="-z-10"
				opacity={1}
				masks={[
					diamondGlows(
						{ x: '-44.5px', y: '269.5px' },
						{ x: 'calc(100% - 15.5px)', y: '288.5px' },
						{ x: '50%', y: '496.5px' },
					),
				]}
			/>
			<div className="mx-auto max-w-[1440px] px-6 xl:px-10">
				{/* Type scale is the Figma export's, down to the odd-looking values: 56px/64px at -0.02em in a
				    1062px block, paragraph 16px/150% solid ink in a 628px one. Don't round them to Tailwind's
				    nearest step — `tracking-tight` is -0.025em, not -0.02em. */}
				<div className="mx-auto flex max-w-[1062px] flex-col items-center gap-6 pt-10 pb-14 text-center md:pt-[60px]">
					{/* Headline and paragraph are one group 8px apart, then 24px to the CTAs, as Figma nests
					    them — a flat gap on all three would space the pair like the CTAs. */}
					<div className="flex flex-col items-center gap-2">
						<h1 className="text-4xl leading-[1.143] font-medium tracking-[-0.02em] text-balance md:text-[3.5rem]">
							{t`We transform the way we shop to build a`} <Squiggle>{t`better world`}</Squiggle>.
						</h1>
						<p className="text-ink max-w-[628px] text-base leading-[1.5]">
							{t`We connect companies, consumers and NGOs through transparent fundraising technology to finance social projects all over the world.`}
						</p>
					</div>
					{/* 44px tall per Figma (10px padding around a 24px icon slot, 14px label). The badge is a
					    SOLID white disc — every other pill in the redesign uses a solid contrasting badge, and a
					    translucent one disappears against the ink. */}
					<div className="flex flex-wrap justify-center gap-3">
						{/* Sends companies to Solutions rather than straight to Contact: the page ends on the
						    contact form anyway, so nothing is lost, and someone who has not read what we do yet
						    is not asked to write a message first. */}
						<Link
							to="/new/solutions"
							className="bg-ink flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
						>
							{t`I'm a company`}
							<span className="text-ink grid size-6 place-items-center rounded-full bg-white">
								<ArrowUpRight className="size-3.5" />
							</span>
						</Link>
						<Link
							to="/new/partner-ngos"
							className="bg-ink flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
						>
							{t`I'm an NGO`}
							<span className="text-ink grid size-6 place-items-center rounded-full bg-white">
								<ArrowUpRight className="size-3.5" />
							</span>
						</Link>
					</div>
				</div>
			</div>
			<Suspense fallback={<div className="min-h-[28rem] pb-16 md:pb-24" />}>
				<Stats />
			</Suspense>
		</section>
	);
}
